import { useMemo, useState, useCallback } from "react";
import {
  useCompanies,
  useCreateCompany,
  useUpdateCompany,
  useUpdateCompanyStatus,
} from "../../../queries";
import type { Company, CreateCompanyRequest } from "@/services/api/companies";
import { Button } from "@/shared/ui/button";
import { Plus } from "lucide-react";
import { CoreSignalGuideButton } from "../guide/CoreSignalGuideButton";
import { CompaniesTable } from "./CompaniesTable";
import { CompanyCreateDialog } from "./CompanyCreateDialog";
import { CompanyEditDialog } from "./CompanyEditDialog";
import { CompanyStatusDialog } from "./CompanyStatusDialog";
import { sortCompanies } from "./companiesUtils";

export function CoreSignalCompaniesPanel() {
  const { data: companies, isLoading } = useCompanies();
  const createCompanyMutation = useCreateCompany();
  const updateCompanyMutation = useUpdateCompany();
  const updateCompanyStatusMutation = useUpdateCompanyStatus();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [statusCompany, setStatusCompany] = useState<Company | null>(null);
  const [sortColumn, setSortColumn] = useState<string | undefined>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        if (sortDirection === "asc") {
          setSortDirection("desc");
        } else {
          setSortColumn(undefined);
          setSortDirection("asc");
        }
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn, sortDirection]
  );

  const sortedCompanies = useMemo(() => {
    if (!companies || !sortColumn) return companies || [];
    return sortCompanies(companies, sortColumn, sortDirection);
  }, [companies, sortColumn, sortDirection]);

  const handleCreateCompany = (data: CreateCompanyRequest) => {
    createCompanyMutation.mutate(data, {
      onSuccess: () => {
        setCreateDialogOpen(false);
      },
    });
  };

  const handleEditCompany = (id: string | number, data: CreateCompanyRequest) => {
    updateCompanyMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          setEditingCompany(null);
        },
      }
    );
  };

  const handleToggleStatus = (company: Company) => {
    setStatusCompany(company);
  };

  const handleConfirmStatusChange = () => {
    if (!statusCompany) return;
    updateCompanyStatusMutation.mutate(
      {
        id: statusCompany.id,
        data: { isActive: !statusCompany.isActive },
      },
      {
        onSuccess: () => {
          setStatusCompany(null);
        },
      }
    );
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-ink-900">Companies</h2>
          <p className="mt-1 text-sm text-ink-500">
            Manage companies and their configurations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CoreSignalGuideButton />
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add company
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-grid-200 bg-white shadow-sm pb-6">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-ink-500">
            Loading companies...
          </div>
        ) : (
          <CompaniesTable
            companies={sortedCompanies}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            onToggleStatus={handleToggleStatus}
            onEdit={setEditingCompany}
            isUpdating={updateCompanyStatusMutation.isPending || updateCompanyMutation.isPending}
          />
        )}
      </div>

      <CompanyCreateDialog
        open={createDialogOpen}
        isLoading={createCompanyMutation.isPending}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreateCompany}
      />

      <CompanyEditDialog
        open={editingCompany !== null}
        company={editingCompany}
        isLoading={updateCompanyMutation.isPending}
        onOpenChange={(open) => !open && setEditingCompany(null)}
        onSubmit={handleEditCompany}
      />

      <CompanyStatusDialog
        open={statusCompany !== null}
        company={statusCompany}
        isLoading={updateCompanyStatusMutation.isPending}
        onOpenChange={(open) => !open && setStatusCompany(null)}
        onConfirm={handleConfirmStatusChange}
      />
    </div>
  );
}
